/**
 * Thin global rail (left, 52px) — workbench chrome + account-level nav.
 * Sidebar toggle, layout, and credit ring live here (not beside the prompt).
 */
import React, { useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, BarChart3, Settings, CreditCard, LayoutGrid, Menu, Users } from 'lucide-react';
import OrgCreditRing from './OrgCreditRing';
import { PortalMenu } from '@stepflow/ui-shared/components/PortalMenu.jsx';
import { isBillingUiEnabled } from '@stepflow/ui-shared/services/stripeBilling';

function navBtnClass(active) {
  return `
    flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors
    ${active
      ? 'bg-white text-blue-600 shadow-sm ring-1 ring-gray-200/80'
      : 'text-gray-500 hover:bg-white/80 hover:text-gray-800'
    }
  `;
}

const railButtonClass =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-white/80 hover:text-gray-800';

const settingsMenuBtnClass =
  'flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-gray-800 hover:bg-gray-50';

export default function GlobalNavRail({
  homePath = '/network/new',
  showUsage = true,
  /** Show workbench-related gear menu; hidden for role USER */
  showWorkbenchSettings = true,
  sidebarCollapsed,
  onToggleSidebarCollapse,
  onOpenTenantSettingsPanel,
  /** The ACCOUNT button (UserMenu) — sits at the rail's foot, directly above the settings gear (owner 2026-10-22). */
  accountSlot = null,
  /** The WORKSPACE switcher — directly above the account button (owner 2026-10-22). */
  workspaceSlot = null,
}) {
  const { pathname } = useLocation();
  const usageActive = pathname === '/usage' || pathname === '/org/usage';
  const pricingActive = pathname === '/pricing';
  const homeActive = pathname === homePath;
  const appsActive = pathname === '/registry';
  const settingsBtnRef = useRef(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);

  return (
    <nav
      className="flex h-full w-[52px] shrink-0 flex-col items-center overflow-y-auto border-r border-gray-200/90 bg-slate-50/90 py-3"
      aria-label="Global"
    >
      {/* Workbench chrome — same footprint as nav icons below */}
      <div className="flex flex-col items-center gap-1" aria-label="Workbench">
        <button
          type="button"
          onClick={onToggleSidebarCollapse}
          aria-expanded={typeof sidebarCollapsed === 'boolean' ? !sidebarCollapsed : undefined}
          aria-label={sidebarCollapsed ? 'Expand network panel' : 'Collapse network panel'}
          title={sidebarCollapsed ? 'Expand panel' : 'Collapse panel'}
          className={railButtonClass}
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        </button>
        <NavLink
          to="/registry"
          className={navBtnClass(appsActive)}
          title="Registry — browse & deploy networks"
          aria-label="Registry — browse and deploy networks"
        >
          <LayoutGrid className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        </NavLink>
        <div className="flex w-full justify-center py-0.5">
          <OrgCreditRing compact />
        </div>
      </div>

      <div className="my-2 h-px w-8 shrink-0 bg-gray-200/90" aria-hidden />

      <div className="flex flex-col items-center gap-1">
        <NavLink
          to={homePath}
          className={navBtnClass(homeActive)}
          title="Home / networks"
          aria-label="Home / networks"
        >
          <Home className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        </NavLink>
        {showUsage ? (
          <NavLink
            to="/usage"
            className={navBtnClass(usageActive)}
            title="Usage & billing (workspace; organization tab for owners/admins)"
            aria-label="Usage and billing"
          >
            <BarChart3 className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </NavLink>
        ) : null}
        {isBillingUiEnabled() ? (
          <NavLink
            to="/pricing"
            className={navBtnClass(pricingActive)}
            title="Plans & pricing (Stripe)"
            aria-label="Plans and pricing"
          >
            <CreditCard className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </NavLink>
        ) : null}
      </div>

      <div className="flex-1" />

      {workspaceSlot ? (
        <div className="mb-1 flex shrink-0 items-center justify-center" data-testid="rail-workspace">{workspaceSlot}</div>
      ) : null}
      {accountSlot ? (
        <div className="mb-1 flex shrink-0 items-center justify-center" data-testid="rail-account">{accountSlot}</div>
      ) : null}

      {showWorkbenchSettings ? (
        <>
          <button
            ref={settingsBtnRef}
            type="button"
            title="Tenant settings"
            aria-label="Workbench settings"
            aria-haspopup="menu"
            aria-expanded={settingsMenuOpen}
            onClick={() => setSettingsMenuOpen((o) => !o)}
            className={navBtnClass(false)}
          >
            <Settings className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </button>
          <PortalMenu
            anchorRef={settingsBtnRef}
            open={settingsMenuOpen}
            onClose={() => setSettingsMenuOpen(false)}
            align="top-right"
            offset={8}
            width={220}
            className="rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            <button
              type="button"
              role="menuitem"
              className={settingsMenuBtnClass}
              onClick={() => {
                setSettingsMenuOpen(false);
                onOpenTenantSettingsPanel?.();
              }}
            >
              <Users className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              Tenant (context & limits)
            </button>
          </PortalMenu>
        </>
      ) : null}
    </nav>
  );
}
